import React from "react";
import {Stack, TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import SpaceBarIcon from "@mui/icons-material/SpaceBar";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import {printoutPalette} from "../../../core/printoutPalette";
import {
    clampFieldHeightMm,
    FIELD_ALIGNS,
    FIELD_LINE_STYLES,
    getFieldAttrs,
    MAX_FIELD_HEIGHT_MM,
    MIN_FIELD_HEIGHT_MM,
} from "../../../blocks/field/printoutFieldOptions";
import type {
    FieldAlign,
    FieldLineStyle,
    PrintoutFieldAttrs,
} from "../../../blocks/field/printoutFieldOptions";
import {PanelField} from "../PrintoutPanelSection";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";
import type {PrintoutBlockSettingsProps} from "./printoutBlockAttributes";

const ALIGN_ICONS: Record<FieldAlign, React.ReactNode> = {
    center: <FormatAlignCenterIcon fontSize="small"/>,
    left: <FormatAlignLeftIcon fontSize="small"/>,
    right: <FormatAlignRightIcon fontSize="small"/>,
};

const LINE_STYLE_ICONS: Record<FieldLineStyle, React.ReactNode> = {
    box: <CheckBoxOutlineBlankIcon fontSize="small"/>,
    underline: <SpaceBarIcon fontSize="small"/>,
};

export function FieldSettings({block, disabled, editor}: PrintoutBlockSettingsProps) {
    const {t} = useTranslation();
    const theme = useTheme();

    const attrs = getFieldAttrs(block.node.attrs as PrintoutFieldAttrs);
    const labelStyleValue = [attrs.labelStyle.bold ? 'bold' : '', attrs.labelStyle.italic ? 'italic' : '']
        .filter(Boolean);

    const update = (next: Partial<PrintoutFieldAttrs>) =>
        updateSelectedPrintoutBlockAttributes(editor, block, next);

    const lineColorPresets = [
        printoutPalette.mutedText,
        printoutPalette.primaryText,
        theme.palette.primary.main,
        printoutPalette.subtleBorder,
    ];
    const fillColorPresets = [
        'transparent',
        printoutPalette.paper,
        printoutPalette.subtleFill,
        theme.palette.primary.main,
    ];

    return (
        <>
            <PanelField label={t('Label')}>
                <TextField
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => update({label: event.target.value})}
                    size={'small'}
                    value={attrs.label}
                />
            </PanelField>

            <Stack direction={'row'} gap={2}>
                <PanelField label={t('Alignment')}>
                    <ToggleButtonGroup
                        disabled={disabled}
                        exclusive
                        onChange={(_event, align: FieldAlign | null) => {
                            if (align) update({align});
                        }}
                        size={'small'}
                        value={attrs.align}
                    >
                        {FIELD_ALIGNS.map(align => (
                            <ToggleButton aria-label={t(align)} key={align} value={align}>
                                {ALIGN_ICONS[align]}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </PanelField>

                <PanelField label={t('Label style')}>
                    <ToggleButtonGroup
                        disabled={disabled}
                        onChange={(_event, next: string[]) => update({
                            labelStyle: {bold: next.includes('bold'), italic: next.includes('italic')},
                        })}
                        size={'small'}
                        value={labelStyleValue}
                    >
                        <ToggleButton aria-label={t('Bold')} value={'bold'}>
                            <FormatBoldIcon fontSize="small"/>
                        </ToggleButton>
                        <ToggleButton aria-label={t('Italic')} value={'italic'}>
                            <FormatItalicIcon fontSize="small"/>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </PanelField>
            </Stack>

            <PanelField label={t('Height mm')}>
                <CNumberField
                    disabled={disabled}
                    fullWidth
                    max={MAX_FIELD_HEIGHT_MM}
                    min={MIN_FIELD_HEIGHT_MM}
                    onChange={(next) => {
                        if (next !== null) update({heightMm: clampFieldHeightMm(next)});
                    }}
                    size={'small'}
                    step={1}
                    value={attrs.heightMm}
                />
            </PanelField>

            <PanelField label={t('Line')}>
                <ToggleButtonGroup
                    disabled={disabled}
                    exclusive
                    fullWidth
                    onChange={(_event, lineStyle: FieldLineStyle | null) => {
                        if (lineStyle) update({lineStyle});
                    }}
                    size={'small'}
                    value={attrs.lineStyle}
                >
                    {FIELD_LINE_STYLES.map(lineStyle => (
                        <ToggleButton aria-label={t(lineStyle)} key={lineStyle} value={lineStyle}>
                            {LINE_STYLE_ICONS[lineStyle]}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </PanelField>

            <PanelField label={t('Line color')}>
                <NewColorPicker
                    disabled={disabled}
                    id="printout-field-line-color"
                    onChange={(value) => update({lineColor: value ?? printoutPalette.mutedText})}
                    presetColors={lineColorPresets}
                    showClear
                    value={attrs.lineColor}
                />
            </PanelField>

            <PanelField label={t('Fill')}>
                <NewColorPicker
                    disabled={disabled}
                    id="printout-field-fill-color"
                    onChange={(value) => update({fillColor: value})}
                    placeholder={t('Transparent')}
                    presetColors={fillColorPresets}
                    showClear
                    value={attrs.fillColor}
                />
            </PanelField>
        </>
    );
}

export default FieldSettings;
